import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';
import apiClient from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PIE_COLORS = {
  paid: '#10b981',
  sent: '#6366f1',
  draft: '#94a3b8',
  overdue: '#ef4444',
};

const MetricCard = ({ label, value, color, icon, delay }) => (
  <div className="card p-6 flex items-start gap-4" style={{ animationDelay: delay }}>
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: color + '18' }}>
      {icon}
    </div>
    <div>
      <p className="text-sm text-slate-500 font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeConnected, setStripeConnected] = useState(false);
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const isOwner = hasRole(ROLES.BUSINESS_OWNER);

  useEffect(() => {
    fetchDashboardData(); if (isOwner) checkStripeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, trendRes, statusRes, invoicesRes] = await Promise.all([
        apiClient.get('/dashboard/metrics'),
        apiClient.get('/dashboard/revenue-trend'),
        apiClient.get('/dashboard/payment-status'),
        apiClient.get('/dashboard/recent-invoices'),
      ]);
      setMetrics(metricsRes.data);
      setRevenueTrend(trendRes.data);
      setPaymentStatus(statusRes.data);
      setRecentInvoices(invoicesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const checkStripeStatus = async () => {
    try { const res = await apiClient.get('/stripe/status'); setStripeConnected(res.data.connected); } catch { }
  };
  const connectStripe = () => {
    navigate('/settings?action=connect-stripe');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
          <p className="text-slate-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div data-testid="dashboard">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">
            {greet()}, {user?.business_name || user?.name || user?.email?.split('@')[0]} 👋
          </h1>
          <p className="text-slate-500 text-sm mt-1">Here's what's happening with your business</p>
        </div>
        {isOwner && (
          <button
            onClick={() => navigate('/invoices/new')}
            className="btn-primary flex items-center gap-2">
            <span>+</span> New Invoice
          </button>
        )}
      </div>

      {/* Stripe banner */}
      {isOwner && !stripeConnected && (
        <div className="mb-6 p-4 rounded-2xl flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #fcd34d' }}
          data-testid="stripe-connect-banner">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💳</span>
            <div>
              <p className="font-semibold text-amber-900">Connect Stripe to accept online payments</p>
              <p className="text-amber-700 text-sm">Start accepting card payments from your clients</p>
            </div>
          </div>
          <button onClick={connectStripe} data-testid="connect-stripe-button"
            className="px-4 py-2 rounded-xl text-sm font-semibold"
            style={{ background: '#f59e0b', color: 'white' }}>
            Connect Stripe
          </button>
        </div>
      )}

      {recentInvoices.length === 0 ? (
        <div className="card p-16 text-center" data-testid="empty-state">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No data yet</h3>
          <p className="text-slate-500 mb-6">Create your first invoice to see dashboard metrics</p>
          {isOwner && (
            <button onClick={() => navigate('/invoices/new')}
              className="btn-primary" data-testid="create-first-invoice-button">
              Create your first invoice
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Outstanding" value={formatCurrency(metrics?.outstanding)} color="#f59e0b" icon="⏳" delay="0ms" />
            <MetricCard label="Paid This Month" value={formatCurrency(metrics?.paid_this_month)} color="#10b981" icon="✅" delay="50ms" />
            <MetricCard label="Overdue" value={metrics?.overdue_count} color="#ef4444" icon="⚠️" delay="100ms" />
            <MetricCard label="Total Revenue" value={formatCurrency(metrics?.total_revenue)} color="#6366f1" icon="💰" delay="150ms" />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="card p-6">
              <h3 className="text-base font-semibold text-slate-900 mb-4" data-testid="revenue-trend-title">
                Revenue Trend
              </h3>
              {revenueTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={revenueTrend} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      formatter={v => [formatCurrency(v), 'Revenue']}
                    />
                    <Bar dataKey="revenue" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-slate-400 text-sm">No revenue data yet</p>}
            </div>

            <div className="card p-6">
              <h3 className="text-base font-semibold text-slate-900 mb-4" data-testid="payment-status-title">
                Payment Status
              </h3>
              {paymentStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={paymentStatus} cx="50%" cy="50%" outerRadius={80} innerRadius={45}
                      dataKey="amount" label={({ status }) => status} labelLine={false}>
                      {paymentStatus.map((entry, i) => (
                        <Cell key={i} fill={PIE_COLORS[entry.status] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => [formatCurrency(v), 'Amount']} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-slate-400 text-sm">No payment data yet</p>}
            </div>
          </div>

          {/* Recent invoices */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900" data-testid="recent-invoices-title">Recent Invoices</h3>
              <button onClick={() => navigate('/invoices')}
                className="text-sm font-medium" style={{ color: '#6366f1' }}>
                View all →
              </button>
            </div>
            <table className="min-w-full" data-testid="recent-invoices-table">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-6 py-4 text-sm font-semibold text-indigo-600">{inv.invoice_number}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{inv.issue_date}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">{formatCurrency(inv.total, inv.currency)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full badge-${inv.status}`}>
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;